import { computed, ref } from "vue";

export const COLOR_MODE_STORAGE_KEY = "heixiu-www-theme";
export const ThemeModes = ["light", "dark"] as const;
export type ThemeMode = (typeof ThemeModes)[number];

/**
 * 实际应用到 <html> 的展示主题。
 * "www" 是官网营销页专属的暗色视觉，由路由 meta 强制应用，
 * 不属于可切换/可持久化的主题模式（主题模式只有 light/dark）。
 */
export type DisplayTheme = ThemeMode | "www";

const SWITCHING_CLASS = "theme-switching";
const theme = ref<DisplayTheme>("light");
let subscribed = false;

function isThemeMode(value: string | null): value is ThemeMode {
  return value !== null && (ThemeModes as readonly string[]).includes(value);
}

export function getStoredTheme(): ThemeMode {
  if (typeof localStorage === "undefined") return "light";
  const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  // 企业/员工端只使用 light/dark；历史遗留的 "www" 偏好按 light 处理
  return stored === "light" || stored === "dark" ? stored : "light";
}

/** Apply a theme atomically and optionally persist it as the user preference. */
export function setColorMode(mode: DisplayTheme, persist = true): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  theme.value = mode;
  root.classList.add(SWITCHING_CLASS);
  root.classList.toggle("dark", mode !== "light");
  root.dataset.theme = mode;
  // Force style calculation so all semantic colors change in the same frame.
  void root.offsetHeight;
  const restore = () => root.classList.remove(SWITCHING_CLASS);
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => requestAnimationFrame(restore));
  } else {
    restore();
  }
  // 只有 light/dark 是用户偏好；"www" 由营销路由强制应用，不写入存储
  if (persist && mode !== "www" && typeof localStorage !== "undefined") {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  }
}

function usesPreferredTheme(path: string): boolean {
  return (
    /^\/(company|employee)(?:\/|$)/.test(path) ||
    /^\/(payment-result|vip-payment-result)(?:\/|$)/.test(path)
  );
}

/** Initialize before mounting; marketing pages always start in the www theme. */
export function initColorMode(): void {
  const path = typeof location === "undefined" ? "/" : location.pathname;
  setColorMode(usesPreferredTheme(path) ? getStoredTheme() : "www", false);
  if (subscribed || typeof globalThis.addEventListener !== "function") return;
  subscribed = true;
  globalThis.addEventListener("storage", (event) => {
    if (event.key !== COLOR_MODE_STORAGE_KEY || !isThemeMode(event.newValue)) return;
    const path = typeof location === "undefined" ? "/" : location.pathname;
    setColorMode(usesPreferredTheme(path) ? event.newValue : "www", false);
  });
}

/** Restore the preference when entering a non-marketing route. */
export function applyPreferredColorMode(): void {
  setColorMode(getStoredTheme(), false);
}

export function useColorMode() {
  const isDark = computed(() => theme.value !== "light");
  function toggle(): void {
    setColorMode(isDark.value ? "light" : "dark");
  }
  function setTheme(mode: ThemeMode): void {
    setColorMode(mode);
  }
  return { theme, isDark, toggle, setTheme };
}
